import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'
import { Separator } from '@/shared/ui/separator'

import { type FC, type PropsWithChildren } from 'react'

import { Root as RadixDropdownRoot } from '@radix-ui/react-dropdown-menu'

import { useSearchContext } from './search/SearchContext'

import { DisplayedValue } from './DisplayedValue'
import { FetchingResultsOption } from './FetchingResults'
import { NoOptionFound } from './NoOptionFound'
import { SelectContent } from './SelectContent'
import { SelectProvider } from './SelectContext'
import {
  SelectAllOption,
  SelectOptionGroup,
  SelectOptionMultiple,
  SelectOptionSingle,
} from './SelectOption'
import { useSelectStateContext } from './SelectStateContext'
import { SelectTrigger } from './SelectTrigger'
import { LocalSearch, Search } from './search'
import type { SelectBlocksTypes, SelectProps } from './types'

/**
 * Select component with single and multiple selection support.
 *
 * A selection component using Radix UI with single, multiple, and grouped selection support.
 * Reuses Input component styling and includes checkboxes for multiple and group modes.
 * Uses Compound Pattern for flexible composition with search.
 *
 * Features:
 * - Single, multiple, or grouped selection mode
 * - Error, disabled states
 * - Sizes sm, md, lg
 * - Animated chevron (rotation) on open/close
 * - Checkboxes for options in multiple and group modes
 * - Grouped selection with indeterminate checkbox (group mode)
 * - Configurable search via Select.Search (remote) and Select.LocalSearch (local)
 * - Ref forwarding
 * - Type-safe props with TypeScript
 *
 * @example
 * ```tsx
 * // Simple select (single selection)
 * <Select
 *   mode="single"
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' }
 *   ]}
 *   value="1"
 *   onChange={(value) => setValue(value)}
 *   placeholder="Select an option"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Multiple select
 * <Select
 *   mode="multiple"
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' },
 *     { value: '3', label: 'Option 3' }
 *   ]}
 *   value={['1', '2']}
 *   onChange={(values) => setValues(values)}
 *   placeholder="Select options"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Select with groups
 * <Select
 *   mode="group"
 *   options={[
 *     {
 *       value: 'fruits',
 *       label: 'Fruits',
 *       type: 'group',
 *       children: [
 *         { value: 'apple', label: 'Apple' },
 *         { value: 'banana', label: 'Banana' }
 *       ]
 *     },
 *     {
 *       value: 'vegetables',
 *       label: 'Vegetables',
 *       type: 'group',
 *       children: [
 *         { value: 'carrot', label: 'Carrot' },
 *         { value: 'tomato', label: 'Tomato' }
 *       ]
 *     }
 *   ]}
 *   value={['apple', 'carrot']}
 *   onChange={(values) => setValues(values)}
 *   placeholder="Select products"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Select with remote search
 * <Select mode="single" options={options} value={value} onChange={setValue}>
 *   <Select.Search onSearch={handleRemoteSearch} placeholder="Search..." />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Select with local search (by label - default)
 * <Select mode="single" options={options} value={value} onChange={setValue}>
 *   <Select.LocalSearch />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Select with custom local search (by value)
 * <Select mode="single" options={options} value={value} onChange={setValue}>
 *   <Select.LocalSearch searchBy="value" />
 * </Select>
 * ```
 *
 * @example
 * ```tsx
 * // Select with search on label and value
 * <Select mode="single" options={options} value={value} onChange={setValue}>
 *   <Select.LocalSearch searchBy="both" placeholder="Search..." />
 * </Select>
 * ```
 */
const SelectRoot: FC<PropsWithChildren<SelectProps>> = ({
  options,
  value,
  onChange,
  placeholder = '',
  error = false,
  disabled = false,
  size = 'md',
  mode = 'single',
  maxTagsDisplayed = 2,
  readonly = false,
  ariaLabel,
  children,
}) => {
  const searchBlocks = getTypedBlocksByTypes<SelectBlocksTypes>(children)

  return (
    <SelectProvider
      options={options}
      value={value}
      onChange={onChange}
      mode={mode}
      size={size}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      readonly={readonly}
      ariaLabel={ariaLabel}
      maxTagsDisplayed={maxTagsDisplayed}
      searchComponent={searchBlocks.SelectLocalSearch || searchBlocks.SelectSearch}
    >
      {mode === 'single' && <SelectSingle />}
      {mode === 'group' && <SelectGroup />}
      {mode === 'multiple' && <SelectMultiple />}
    </SelectProvider>
  )
}

/**
 * Common props for SelectSingle and SelectMultiple sub-components
 */
const SelectSingle: FC = () => {
  const { disabled, readonly, ariaLabel, error, size, isOpen, setIsOpen } = useSelectStateContext()
  const { searchComponent, filteredOptions, loading } = useSearchContext()

  let content
  if (loading) {
    content = <FetchingResultsOption />
  } else if (filteredOptions.length === 0) {
    content = <NoOptionFound />
  } else {
    content = filteredOptions.map(option => (
      <SelectOptionSingle key={option.value} option={option} />
    ))
  }
  return (
    <RadixDropdownRoot
      open={readonly ? false : isOpen}
      onOpenChange={readonly ? undefined : setIsOpen}
    >
      <SelectWrapper error={error} disabled={disabled} readonly={readonly} size={size}>
        <SelectTrigger
          isOpen={isOpen}
          mode="single"
          disabled={disabled}
          readonly={readonly}
          ariaLabel={ariaLabel}
        >
          <DisplayedValue />
        </SelectTrigger>
        <SelectContent mode="single" header={searchComponent} size={size}>
          {content}
        </SelectContent>
      </SelectWrapper>
    </RadixDropdownRoot>
  )
}

SelectSingle.displayName = 'SelectSingle'

/**
 * Internal component for rendering in multiple mode
 * Uses Radix DropdownMenu for multi-selection behavior with checkboxes
 */
const SelectMultiple: FC = () => {
  const { disabled, readonly, ariaLabel, error, size, isOpen, setIsOpen } = useSelectStateContext()
  const { searchComponent, filteredOptions, loading } = useSearchContext()

  let selectAll = null
  if (!loading) {
    selectAll = <SelectAllOption />
  }
  let content
  if (loading) {
    content = <FetchingResultsOption />
  } else if (filteredOptions.length === 0) {
    content = <NoOptionFound />
  } else {
    content = filteredOptions.map(option => (
      <SelectOptionMultiple key={option.value} option={option} />
    ))
  }
  return (
    <RadixDropdownRoot
      open={readonly ? false : isOpen}
      onOpenChange={readonly ? undefined : setIsOpen}
    >
      <SelectWrapper error={error} disabled={disabled} readonly={readonly} size={size}>
        <SelectTrigger
          isOpen={isOpen}
          mode="multiple"
          disabled={disabled}
          readonly={readonly}
          ariaLabel={ariaLabel}
        >
          <DisplayedValue />
        </SelectTrigger>
        <SelectContent mode="multiple" header={searchComponent} size={size}>
          <div className="select-select-all">
            {selectAll}
            <Separator />
          </div>
          {content}
        </SelectContent>
      </SelectWrapper>
    </RadixDropdownRoot>
  )
}

SelectMultiple.displayName = 'SelectMultiple'

/**
 * Internal component for rendering in group mode
 * Uses Radix DropdownMenu for multi-selection behavior with checkboxes and groups
 */
const SelectGroup: FC = () => {
  const { disabled, readonly, ariaLabel, error, size, isOpen, setIsOpen } = useSelectStateContext()
  const { searchComponent, filteredOptions, loading } = useSearchContext()

  let selectAll = null
  if (!loading) {
    selectAll = <SelectAllOption />
  }
  let content
  if (loading) {
    content = <FetchingResultsOption />
  } else if (filteredOptions.length === 0) {
    content = <NoOptionFound />
  } else {
    content = filteredOptions.map(option => {
      if (option.children && option.children.length > 0) {
        return <SelectOptionGroup key={option.value} option={option} />
      }
      return <SelectOptionMultiple key={option.value} option={option} />
    })
  }
  return (
    <RadixDropdownRoot
      open={readonly ? false : isOpen}
      onOpenChange={readonly ? undefined : setIsOpen}
    >
      <SelectWrapper error={error} disabled={disabled} readonly={readonly} size={size}>
        <SelectTrigger
          isOpen={isOpen}
          mode="group"
          disabled={disabled}
          readonly={readonly}
          ariaLabel={ariaLabel}
        >
          <DisplayedValue />
        </SelectTrigger>
        <SelectContent mode="group" header={searchComponent} size={size}>
          <div className="select-select-all">
            {selectAll}
            <Separator />
          </div>
          {content}
        </SelectContent>
      </SelectWrapper>
    </RadixDropdownRoot>
  )
}

SelectGroup.displayName = 'SelectGroup'

/**
 * Internal component for the wrapper with common CSS classes
 */
const SelectWrapper: FC<
  PropsWithChildren<{
    error: boolean
    disabled: boolean
    readonly: boolean
    size: SelectProps['size']
  }>
> = ({ error, disabled, readonly, size, children }) => {
  return (
    <div
      className={cn('selectw-wrapper', {
        'selectw-error': error,
        'selectw-disabled': disabled,
        'selectw-readonly': readonly,
        'selectw-sm': size === 'sm',
        'selectw-md': size === 'md',
        'selectw-lg': size === 'lg',
      })}
    >
      {children}
    </div>
  )
}

SelectRoot.displayName = 'Select'

export const Select = Object.assign(SelectRoot, {
  Search: Search,
  LocalSearch: LocalSearch,
})
